import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Position } from '@kemu-io/kemu-core/types';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DragLayerMonitor, useDrag } from 'react-dnd';
import { createPortal } from 'react-dom';
import { usePreview } from 'react-dnd-preview';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import { DroppableTypes } from '@common/constants';
import { pasteFromClipboardAction } from '@src/features/LogicMapper/reducers/clipboardReducers';
import { selectCurrentRecipe } from '@src/features/Workspace/workspaceSlice';
import useGetCurrentCanvasZoom from '@hooks/useGetCurrentCanvasZoom';
import useKemuNotification from '@hooks/useKemuNotification';
import useTranslation from '@hooks/useTranslation';
import { LogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext';
import styles from './Snippetify.module.css';
import { usePreviewStateFull } from 'react-dnd-preview/dist/cjs/usePreview';
import { ReactComponent as SnippetIcon } from '@src/assets/img/menus/snippets.svg';
import Icon from '@ant-design/icons';
import { getDecodedClipboardData } from '@src/features/LogicMapper/reducers/clipboardUtils';
import classNames from 'classnames';
import Spinner from '@components/spinner/spinner';

export type DropCoordinates = {
  x: number;
  y: number;
}

type OnBeforeDropResult = {
  coordinates?: DropCoordinates;
  snippetCode?: string;
}

type Props = {
  snippetCode: string;
  meta?: any;
  children: React.ReactNode;
  dragIcon?: React.ReactNode;
  dragPreviewWrapperClass?: string;
  onIsDragging?: (isDragging: boolean) => void;
  onBeforeDrop?: (coordinates: DropCoordinates, meta?: any) => Promise<OnBeforeDropResult>;
}

type SnippetifyDragItem = {
  type: string;
  code: string;
  rect: DOMRect | null;
}

const PreviewSize = {
  width: 80,
  height: 80,
};

/**
 * Calculates the click offset for drag operations
 * @param monitor - The drag layer monitor
 * @returns Object containing x and y offset values
 */
const calculateClickOffset = (monitor: DragLayerMonitor) => {
  const initialClickPosition = monitor.getInitialClientOffset();
  const sourceOffset = monitor.getInitialSourceClientOffset();

  return {
    x: (initialClickPosition?.x || 0) - (sourceOffset?.x || 0),
    y: (initialClickPosition?.y || 0) - (sourceOffset?.y || 0),
  };
};

const DragPreview = (props: {
  onCalculatedDiff?: (diff: { x: number, y: number }) => void;
  onPreviewStyleChange?: (style: React.CSSProperties) => void;
  dragIcon?: React.ReactNode;
  dragPreviewWrapperClass?: string;
}) => {
  const { onCalculatedDiff, onPreviewStyleChange, dragIcon, dragPreviewWrapperClass } = props;
  const { logicMapperContainerEl } = useContext(LogicMapperContext);
  const { display, style, itemType, item, monitor } = usePreview() as usePreviewStateFull<SnippetifyDragItem>;
  const diffRef = useRef<{ x: number, y: number } | null>(null);
  const abortRendering = itemType !== DroppableTypes.KemuSnippet
    || !display
    || !logicMapperContainerEl;


  useEffect(() => {
    if (!abortRendering && !diffRef.current) {
      const clickDiff = calculateClickOffset(monitor);
      diffRef.current = clickDiff;
      // IMPORTANT: We report the initial click offset so that the drop location
      // can be adjusted based on where in the image the drag started
      onCalculatedDiff?.(diffRef.current);
    }
  }, [monitor, onCalculatedDiff, abortRendering]);

  const previewStyle = useMemo(() => {
    return {
      ...style,
      left: (diffRef.current?.x || 0) - PreviewSize.width / 2,
      top: (diffRef.current?.y || 0) - PreviewSize.height / 2,
      width: PreviewSize.width,
      height: PreviewSize.height,
    };
  }, [style, diffRef.current]);

  // Update parent with last preview position
  useEffect(() => {
    if (style) {
      onPreviewStyleChange?.(previewStyle);
    }
  }, [previewStyle, onPreviewStyleChange]);

  if (abortRendering || !diffRef.current) {
    return null;
  }
 
  // NOTE: we must use a portal because the ServiceHelpPanel uses transforms that would break the 'fixed' position of this element
  return createPortal(
    <div
      className={classNames(styles.PreviewWrapper, dragPreviewWrapperClass)}
      style={previewStyle}
    >
      <div className={styles.DragPreviewContent}>
        {dragIcon || <Icon component={SnippetIcon} />}
      </div>
    </div>,
    logicMapperContainerEl
  );
};

/**
 * A wrapper component that makes any child element draggable as a Kemu snippet
 * @param snippetCode - The snippet code string to be pasted when dropped
 * @param children - The child elements to make draggable
 * @param dragIcon - Optional custom icon to display during drag operations
 * @param dragPreviewWrapperClass - Optional CSS class to add to the drag preview wrapper
 * @param onIsDragging - Optional callback for drag state changes
 * @param onBeforeDrop - Optional async function to transform coordinates and snippet code before drop
 */
const Snippetify = (props: Props) => {
  const dragRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const dragSourceDiff = useRef<DropCoordinates | null>(null);
  const currentRecipe = useSelector(selectCurrentRecipe);
  const getZoomLevel = useGetCurrentCanvasZoom();
  const kemuNotification = useKemuNotification();
  const t = useTranslation();
  const [isFetchingSnippetCode, setIsFetchingSnippetCode] = useState<boolean>(false);
  // const [previewStyle, setPreviewStyle] = useState<React.CSSProperties | null>(null);
  const previewStyleRef = useRef<React.CSSProperties | null>(null);
  const { snippetCode, children, dragIcon, dragPreviewWrapperClass, onIsDragging, onBeforeDrop, meta } = props;

  const handleCalculatedDiff = useCallback((diff: DropCoordinates) => {
    dragSourceDiff.current = diff;
  }, []);

  const handlePreviewStyleChange = useCallback((style: React.CSSProperties) => {
    previewStyleRef.current = style;
  }, []);


  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [{ isDragging }, drag, preview] = useDrag<any, Position, any>(() => ({
    type: DroppableTypes.KemuSnippet,
    item: () => {
      const node = dragRef.current;
      // Capture initial state when drag starts
      const rect = node ? node.getBoundingClientRect() : null;

      return {
        rect,
      };
    },
    end: async (item, monitor) => {
      const dropLocation = monitor.getDropResult();
      const zoom = getZoomLevel();
      if (dropLocation && currentRecipe.poolId) {
        const clickDiff = dragSourceDiff.current;
        const diffX = clickDiff?.x || 0;
        const diffY = clickDiff?.y || 0;

        // Adjust the drop location based on where in the element the click happened
        // and the current zoom level
        const adjustedDropLocation = {
          x: (dropLocation.x + (diffX / zoom) - (PreviewSize.width / 2) / zoom) * zoom,
          y: (dropLocation.y + (diffY / zoom) - (PreviewSize.height / 2) / zoom) * zoom,
        };

        let finalCoordinates = adjustedDropLocation;
        let finalSnippetCode = snippetCode;
        let abortDrop = false;
        // Call onBeforeDrop if provided to allow coordinate and snippet code transformation
        if (onBeforeDrop) {
          try {
            setIsFetchingSnippetCode(true);
            const result = await onBeforeDrop(adjustedDropLocation, meta);
            if (result.coordinates) {
              finalCoordinates = result.coordinates;
            }
            if (result.snippetCode) {
              finalSnippetCode = result.snippetCode;
            }
          } catch (error) {
            console.error('Error in onBeforeDrop:', error);
            abortDrop = true;
            // abort dropping on error
            kemuNotification({
              code: 'snippet-drop-error',
              message: t('Snippetify.Error.DropFailed'),
              type: 'error',
            });
            // Continue with original values if onBeforeDrop fails
          } finally {
            setIsFetchingSnippetCode(false);
            previewStyleRef.current = null;
          }
        }

        if (abortDrop) {
          return;
        }

        const decoded = getDecodedClipboardData(finalSnippetCode);
        if (decoded) {
          dispatch(pasteFromClipboardAction({
            recipeId: currentRecipe.poolId,
            thingId: DEFAULT_THING_ID,
            mapData: decoded,
            mouseLocation: finalCoordinates,
          }));
        }
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [currentRecipe, getZoomLevel, snippetCode, onBeforeDrop, meta, t]);

  useEffect(() => {
    const emptyImage = getEmptyImage();
    preview(emptyImage);
  }, [preview]);

  // Report dragging state: keep "dragging" true while either actively dragging or fetching snippet code
  useEffect(() => {
    onIsDragging?.(isDragging || isFetchingSnippetCode);
  }, [isDragging, isFetchingSnippetCode, onIsDragging]);

  // Connect the dragRef to the drag function
  drag(dragRef);

  return (
    <div ref={dragRef} className={styles.ChildWrapper}>
      {children}

      {isDragging && !isFetchingSnippetCode && (
        <DragPreview 
          onCalculatedDiff={handleCalculatedDiff} 
          onPreviewStyleChange={handlePreviewStyleChange}
          dragIcon={dragIcon} 
          dragPreviewWrapperClass={dragPreviewWrapperClass}
        />
      )}

      {isFetchingSnippetCode && previewStyleRef.current && (
        <div 
          className={classNames(styles.PreviewWrapper, dragPreviewWrapperClass)} 
          style={previewStyleRef.current}
        >
            <Spinner loading={true}>
          <div className={styles.DragPreviewContent}>
              {dragIcon || <Icon component={SnippetIcon} />}
          </div>
            </Spinner>
        </div>
      )}
    </div>
  );
};

export default Snippetify;
